import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';

// Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { CycleListPage } from '@/pages/cycles/CycleListPage';
import { CycleDetailPage } from '@/pages/cycles/CycleDetailPage';
import { FeedManagementPage } from '@/pages/feed/FeedManagementPage';
import { PigSalesPage } from '@/pages/sales/PigSalesPage';
import { SettlementsPage } from '@/pages/settlements/SettlementsPage';
import { SettlementWorkspacePage } from '@/pages/settlements/SettlementWorkspacePage';
import { LedgerPage } from '@/pages/ledger/LedgerPage';
import { ReportsPage } from '@/pages/reports/ReportsPage';
import { ReviewAndResolvePage } from '@/pages/admin/ReviewAndResolvePage';
import { MasterDataPage } from '@/pages/admin/MasterDataPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { DocumentsPage } from '@/pages/documents/DocumentsPage';
import { BugReportsPage } from '@/pages/admin/BugReportsPage';
import { BugReportProvider } from '@/context/BugReportContext';

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BugReportProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Auth Routes */}
              <Route path="/login" element={<LoginPage />} />

              {/* Authenticated Application Shell */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="cycles" element={<CycleListPage />} />
                <Route path="cycles/:id" element={<CycleDetailPage />} />
                <Route path="cycles/:id/settlement" element={<SettlementWorkspacePage />} />
                <Route path="feed" element={<FeedManagementPage />} />
                <Route path="sales" element={<PigSalesPage />} />
                <Route path="settlements" element={<SettlementsPage />} />
                <Route path="ledger" element={<LedgerPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="documents" element={<DocumentsPage />} />

                {/* Admin-only Modules */}
                <Route
                  path="admin/bugs"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <BugReportsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/review-and-resolve"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <ReviewAndResolvePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/master-data"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <MasterDataPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/users"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="admin/documents" element={<Navigate to="/documents" replace />} />
              </Route>

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </BugReportProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

